import { Box, Dialog, DialogContent, Paper, Skeleton } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"

import { PAYABLE_APPROVALS_SEARCH_PLACEHOLDER } from "../constants"
import { usePayableApprovalsList } from "../hooks/usePayableApprovalsList"
import type { PayableApprovalsSearchParams } from "../types/searchParams"

import { PayableApprovalsFilterChips } from "./filters/PayableApprovalsFilterChips"
import { PayableApprovalsFilterDrawer } from "./filters/PayableApprovalsFilterDrawer"
import { InvoiceReviewDialog } from "./InvoiceReviewDialog/InvoiceReviewDialog"
import { PayableApprovalsTable } from "./PayableApprovalsTable"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { Invoice } from "@/graphql/codegen/graphql"
import { useGetInvoice } from "@/screens/Accounting/subfeatures/Invoices/hooks/useGetInvoice"
import { useCurrentUserStore } from "@/stores/currentUserStore"

export const PayableApprovalsListPage: FC = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [activeReviewInvoiceId, setActiveReviewInvoiceId] = useState<string | null>(null)

  const currentUserId = useCurrentUserStore((state) => state.currentUser?.userId)

  const {
    invoices,
    isFetching,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = usePayableApprovalsList()

  const handleDueDateRangeRemove = useCallback(
    () => handleFilterRemove(["dueDateFrom", "dueDateTo"]),
    [handleFilterRemove]
  )

  const {
    invoice: activeReviewInvoice,
    isFetching: isFetchingActiveInvoice,
    refetch: refetchActiveReviewInvoice,
  } = useGetInvoice({
    invoiceId: activeReviewInvoiceId ?? undefined,
  })

  const handleFilterClick = useCallback(() => {
    setIsFilterDrawerOpen(true)
  }, [])

  const handleFilterDrawerClose = useCallback(() => {
    setIsFilterDrawerOpen(false)
  }, [])

  const handleReview = useCallback((row: Invoice) => {
    setActiveReviewInvoiceId(row.invoiceId)
  }, [])

  const handleReviewDialogClose = useCallback(() => {
    setActiveReviewInvoiceId(null)
  }, [])

  // Mount the dialog as soon as the row's Review is clicked. When cached data is
  // available the real dialog renders immediately while urql's `cache-and-network`
  // policy revalidates in the background. The skeleton bridges the first-open case.
  const isReviewDialogOpen = Boolean(activeReviewInvoiceId)
  const isReviewSkeletonVisible = isReviewDialogOpen && !activeReviewInvoice && isFetchingActiveInvoice

  return (
    <Box className='flex size-full flex-col'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
        <TableHeaderActions<PayableApprovalsSearchParams>
          currentSearch={searchParams}
          placeholder={PAYABLE_APPROVALS_SEARCH_PLACEHOLDER}
          navigateTo='/app/accounting/payable-approvals'
          testIdPrefix='payable-approvals'
        >
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={handleFilterClick}
            rootElementName='button'
            data-testid='payable-approvals.filter-button'
          >
            Filter
          </Button>
        </TableHeaderActions>

        <PayableApprovalsFilterChips searchParams={searchParams} onRemove={handleDueDateRangeRemove} />

        <Box className='flex flex-1 flex-col overflow-y-auto'>
          <PayableApprovalsTable
            data={invoices}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            loading={isFetching}
            onScroll={handleScroll}
            containerRef={tableContainerRef}
            onReview={handleReview}
          />
        </Box>

        <PayableApprovalsFilterDrawer
          open={isFilterDrawerOpen}
          onClose={handleFilterDrawerClose}
          searchParams={searchParams}
          onChange={handleFilterChange}
          onBatchChange={handleFiltersChange}
          onClear={handleFilterClear}
        />
      </Paper>

      {isReviewSkeletonVisible && (
        <Dialog
          open
          onClose={handleReviewDialogClose}
          maxWidth='md'
          fullWidth
          data-testid='accounting.invoice-review-dialog.loading-skeleton'
        >
          <DialogHeader title='Review Invoice' onClose={handleReviewDialogClose} />
          <DialogContent>
            <Box className='flex flex-col gap-3 py-2'>
              <Skeleton variant='rectangular' height={24} />
              <Skeleton variant='rectangular' height={24} />
              <Skeleton variant='rectangular' height={24} />
              <Skeleton variant='rectangular' height={24} />
              <Skeleton variant='rectangular' height={24} />
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {isReviewDialogOpen && activeReviewInvoice && (
        <InvoiceReviewDialog
          invoice={activeReviewInvoice}
          currentUserId={currentUserId}
          onClose={handleReviewDialogClose}
          onSuccess={refetch}
          onRefresh={refetchActiveReviewInvoice}
        />
      )}
    </Box>
  )
}
